import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { trackPageview, startEngagementTracking, isPrivatePath } from '@/utils/analytics'
import Home from '@/views/Home.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'register', 
      component: () => import('@/views/Register.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/mijn-reserveringen',
      name: 'my-bookings',
      component: () => import('@/views/MyBookings.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/profiel',
      name: 'profile',
      component: () => import('@/views/Profile.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/email-verification',
      name: 'email-verification',
      component: () => import('@/views/EmailVerification.vue')
    },
    {
      path: '/wachtwoord-vergeten',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPassword.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/wachtwoord-resetten',
      name: 'reset-password',
      component: () => import('@/views/ResetPassword.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reservering/:confirmationCode',
      name: 'booking-details',
      component: () => import('@/views/BookingDetails.vue'),
      props: true
    },
    {
      path: '/booking-payment/:bookingId',
      name: 'booking-payment',
      component: () => import('@/views/BookingPayment.vue'),
      props: true
    },
    {
      path: '/booking-success/:checkoutSessionId',
      name: 'booking-success',
      component: () => import('@/views/BookingSuccess.vue'),
      props: true
    },
    {
      path: '/credit-packages',
      name: 'credit-packages',
      component: () => import('@/views/CreditPackages.vue')
    },
    {
      path: '/credit-purchase-success/:checkoutSessionId',
      name: 'credit-purchase-success',
      component: () => import('@/views/CreditPurchaseSuccess.vue'),
      props: true
    },
    {
      path: '/cadeaubon',
      name: 'gift-card',
      component: () => import('@/views/GiftCardPurchase.vue')
    },
    {
      path: '/cadeaubon-success/:checkoutSessionId',
      name: 'gift-card-success',
      component: () => import('@/views/GiftCardSuccess.vue'),
      props: true
    },
    {
      path: '/cadeaubon-inwisselen',
      name: 'gift-card-redeem',
      component: () => import('@/views/GiftCardRedeem.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/evenementen',
      name: 'events',
      component: () => import('@/views/EventList.vue')
    },
    {
      path: '/evenementen/:slug',
      name: 'event-detail',
      component: () => import('@/views/EventDetail.vue'),
      props: true
    },
    {
      path: '/evenement-bevestiging/:sessionId',
      name: 'event-registration-success',
      component: () => import('@/views/EventRegistrationSuccess.vue'),
      props: true
    },
    {
      path: '/mijn-evenementen',
      name: 'my-event-registrations',
      component: () => import('@/views/MyEventRegistrations.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/profielen',
      name: 'talent-list',
      component: () => import('@/views/TalentList.vue')
    },
    {
      path: '/profielen/inschrijven',
      name: 'talent-signup',
      component: () => import('@/views/TalentSignup.vue')
    },
    {
      path: '/profielen/:slug',
      name: 'talent-detail',
      component: () => import('@/views/TalentDetail.vue'),
      props: true
    },
    {
      path: '/profiel-beheer',
      name: 'talent-manage',
      component: () => import('@/views/TalentManage.vue')
    },
    {
      path: '/mijn-berichten',
      name: 'my-talent-conversations',
      component: () => import('@/views/MyTalentConversations.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/profielen',
      name: 'admin-talent-profiles',
      component: () => import('@/views/AdminTalentProfiles.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/profielen/:id',
      name: 'admin-talent-profile-detail',
      component: () => import('@/views/AdminTalentProfileDetail.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/profielen-berichten',
      name: 'admin-talent-messages',
      component: () => import('@/views/AdminTalentMessages.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/Admin.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/schema',
      name: 'schedule-management',
      component: () => import('@/views/ScheduleManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/nieuwe-reservering',
      name: 'admin-booking-create',
      component: () => import('@/views/AdminBookingCreate.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      component: () => import('@/views/Admin.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/bookings',
      name: 'admin-bookings',
      component: () => import('@/views/AdminBookings.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/schedule-templates',
      name: 'admin-schedule-templates',
      component: () => import('@/views/ScheduleManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/blocked-slots',
      name: 'admin-blocked-slots',
      component: () => import('@/views/AdminBlockedSlots.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/pricing-rates',
      name: 'admin-pricing-rates',
      component: () => import('@/views/AdminPricing.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/settings',
      name: 'admin-settings',
      component: () => import('@/views/AdminSettings.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/customers',
      name: 'admin-customers',
      component: () => import('@/views/AdminCustomers.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/audit-logs',
      name: 'admin-audit-logs',
      component: () => import('@/views/AdminAuditLogs.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/discount-codes',
      name: 'admin-discount-codes',
      component: () => import('@/views/AdminDiscountCodes.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/loyalty-config',
      name: 'admin-loyalty-config',
      component: () => import('@/views/AdminLoyaltyConfig.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/leads',
      name: 'admin-booking-leads',
      component: () => import('@/views/AdminBookingLeads.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/contactberichten',
      name: 'admin-contact-messages',
      component: () => import('@/views/AdminContactMessages.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/cadeaubonnen',
      name: 'admin-gift-cards',
      component: () => import('@/views/AdminGiftCards.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/krediet-aankopen',
      name: 'admin-credit-purchases',
      component: () => import('@/views/AdminCreditPurchases.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/evenementen',
      name: 'admin-events',
      component: () => import('@/views/AdminEvents.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/evenementen/nieuw',
      name: 'admin-event-create',
      component: () => import('@/views/AdminEventEdit.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/evenementen/:id',
      name: 'admin-event-edit',
      component: () => import('@/views/AdminEventEdit.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/evenementen/:id/inschrijvingen',
      name: 'admin-event-registrations',
      component: () => import('@/views/AdminEventRegistrations.vue'),
      props: true,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/g/:token',
      name: 'booth-gallery',
      component: () => import('@/views/BoothGallery.vue'),
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/privacybeleid',
      name: 'privacy-policy',
      component: () => import('@/views/PrivacyPolicy.vue')
    },
    {
      path: '/algemene-voorwaarden',
      name: 'terms-of-service',
      component: () => import('@/views/TermsOfService.vue')
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  
  // Only check authentication for protected routes
  const requiresAuth = to.meta.requiresAuth || to.meta.requiresAdmin
  
  // If route requires auth and we have a token but no user, try to fetch user
  if (requiresAuth && authStore.token && !authStore.user) {
    try {
      await authStore.fetchUser()
    } catch (error) {
      // If fetching user fails, clear invalid token
      authStore.clearAuthState()
    }
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }
  
  // Check if route requires admin privileges
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next({ name: 'home' })
    return
  }
  
  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next({ name: 'my-bookings' })
    return
  }
  
  next()
})

// Track SPA pageviews + engagement via Umami (skip admin internal dashboard
// and the private booth gallery)
router.afterEach((to) => {
  if (to.path.startsWith('/admin') || isPrivatePath(to.path)) return
  trackPageview()
  startEngagementTracking(to.path)
})

export default router